import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, shareReplay, takeUntil, tap } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { NoticeDialogComponent } from '../components/notice/notice-dialog.component';
import {
	AddListingItemToCartRequestModel,
	CartDetailsModel,
	CartSummaryModel,
	CreatePurchaseRequestModel,
	CreatePurchaseResponseModel,
	GetPurchaseDetailsResponseModel,
	GetShippingSummaryForDeliveryModel,
	VaildCartModel,
} from '../models/cart.models';
import {
	GetListingDetailsResponseModel,
	GetWineListingsLevelTwoPurchaseOptionModel,
} from '../models/view-listings.models';
import { AuthService } from './auth.service';
import { GtmEventsService } from './gtm-events.service';
import { SessionStorageStorageService } from './session-storage.service';

@Injectable({
	providedIn: 'root',
})
export class CartService implements OnDestroy {
	private searchEvent = new Subject<any>();

	cartChanged$: Subject<boolean> = new Subject<boolean>();
	searchEvent$ = this.searchEvent.asObservable();

	cartSummary: CartSummaryModel;
	timeRemaining: string;
	timeUnderTenMin: boolean = false;

	ngUnsubscribe$ = new Subject();

	localStorage = window.localStorage;
	timerInterval: any;

	// Provide a cached copy of the latest cart details.
	private _cartDetailsData$ = new BehaviorSubject<CartDetailsModel>(undefined);
	public cachedCartDetailsData$ = this._cartDetailsData$.pipe(
		filter((x) => !!x),
		shareReplay(1),
	);

	private get baseApiUrl(): string {
		return environment.marketplaceUrl;
	}

	constructor(
		private http: HttpClient,
		private toastr: ToastrService,
		private authService: AuthService,
		public dialog: MatDialog,
		private router: Router,
		private sessionStorage: SessionStorageStorageService,
		private gtmEventsService: GtmEventsService,
	) {}

	/**
	 * Adds an item to the cart, if no cart exists, one will be created and the item added. This method is to be called from components.
	 * @param listingItemId The listing item Id of the item to be added to the cart
	 */
	addBottleToCart(
		quantity: number,
		listingId: number,
		bottleDetails?: GetWineListingsLevelTwoPurchaseOptionModel | GetListingDetailsResponseModel,
	): void {
		const referralCode = this.sessionStorage.read('ReferralCode') ?? null;
		this.addBottleToCartRequestV1(quantity, listingId, referralCode)
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe({
				next: (result) => {
					if (result) {
						this.localStorage.setItem('marketplaceCartId', result.id);
						this.cartSummary = result;
						this.cartChanged$.next(true);
						this.setCountdownTimer(this.cartSummary.expireOn);
						this.toastr.success('Successfully added items to cart', 'Added to cart');

						this.gtmEventsService.addToCartEvent(quantity, bottleDetails);
						return;
					}
					// If the api returned null that means the cart is no longer valid, clear everything
					clearInterval(this.timerInterval);
					this.cartSummary = null;
					this.cartChanged$.next(true);
					this.localStorage.removeItem('marketplaceCartId');
				},
				error: () => {
					this.toastr.error(
						'An error has occurred, please try again. If the issue persists, please contact Wine Ark.',
						'Error occurred',
					);
				},
			});
	}

	/**
	 * Handles the HTTP request for adding a bottle to a cart,
	 * @param listingItemId The listing item to be added to the cart.
	 * @returns An observable of a Cart Summary or null (if the cart is no longer valid).
	 */
	private addBottleToCartRequestV1(
		quantity: number,
		listingId: number,
		referralCode: string | null,
	): Observable<CartSummaryModel> {
		const url = `${this.baseApiUrl}/v1/MyCart`;
		const model: AddListingItemToCartRequestModel = {
			cartId: this.cartSummary?.id ?? null,
			quantity: quantity,
			listingId: listingId,
			referralCode: referralCode,
		};
		return this.http.post<CartSummaryModel>(url, model);
	}

	getCartSummaryV1(cartId: string): Observable<CartSummaryModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${cartId}/summary`;
		return this.http.get<CartSummaryModel>(url, {});
	}

	getCartSummaryRequestV1(): Observable<CartSummaryModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary?.id}/summary`;
		return this.http.get<CartSummaryModel>(url, {});
	}

	getCartDetailsV1(): Observable<CartDetailsModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary?.id}`;
		const ob$ = this.http.get<CartDetailsModel>(url, {});

		return ob$.pipe(
			tap((v) => {
				this._cartDetailsData$.next(v);
			}),
		);
	}

	getPurchaseDetailsV1(referenceNumber: string): Observable<GetPurchaseDetailsResponseModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/purchase/${referenceNumber}`;
		return this.http.get<GetPurchaseDetailsResponseModel>(url, {});
	}

	getGuestPurchaseDetailsV1(referenceNumber: string): Observable<GetPurchaseDetailsResponseModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/purchase/${referenceNumber}/guest`;
		return this.http.get<GetPurchaseDetailsResponseModel>(url, {});
	}

	extendCart(): void {
		this.extendCartV1()
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe({
				next: (summary) => {
					this.toastr.success('Cart timer has been extended by 10 minutes.', 'Cart extended');
					this.cartSummary = summary;
					this.cartChanged$.next(true);
					this.setCountdownTimer(this.cartSummary.expireOn);
					return;
				},
				error: () => {
					// If it fails just means it was already extended
				},
			});
	}

	extendCartV1(): Observable<CartSummaryModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary?.id}/extend`;
		return this.http.put<CartSummaryModel>(url, {});
	}

	/**
	 * Get a list of sites that are included in a cart, use response to calculate costs in billing api.
	 * @returns Summary of sites included in a cart.
	 */
	getDeliverySummaryForDeliveryV1(): Observable<GetShippingSummaryForDeliveryModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary.id}/ShippingDetails`;
		return this.http.get<GetShippingSummaryForDeliveryModel>(url, {});
	}

	removeBottleFromCart(cartItemId: number): void {
		this.removeBottleFromCartRequestV1(cartItemId)
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe({
				next: (cartSummary) => {
					if (cartSummary) {
						this.cartSummary = cartSummary;
						this.cartChanged$.next(true);
						this.setCountdownTimer(cartSummary.expireOn);
					} else {
						// If this API returned null, that means that the cart is now empty and therefor destroyed, so clear cart Id
						clearInterval(this.timerInterval);
						this.timeRemaining = null;
						this.cartSummary = null;
						this.cartChanged$.next(false);
						this.localStorage.removeItem('marketplaceCartId');
					}
				},
				error: () => {
					this.toastr.error(
						'An error has occurred, please try again. If the issue persists, please contact Wine Ark.',
						'Error occurred',
					);
				},
			});
	}

	/**
	 * Handles the HTTP request for removing a bottle from the cart. If the cart will become empty, the cart is destroyed.
	 * @param cartItemId The cart item id of the item to remove from the cart.
	 * @returns Observable of the summary of the cart (or null if the cart is no longer valid).
	 */
	private removeBottleFromCartRequestV1(cartItemId: number): Observable<CartSummaryModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary?.id}/items/${cartItemId}`;
		return this.http.delete<CartSummaryModel>(url, {});
	}

	removeCartItemsByCartItemId(cartItemsId: string[]): void {
		this.removeCartItemsByCartItemIdRequestV1(cartItemsId)
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe({
				next: (cartSummary) => {
					if (cartSummary) {
						this.cartSummary = cartSummary;
						this.cartChanged$.next(true);
						this.setCountdownTimer(cartSummary.expireOn);
					} else {
						// If this API returned null, that means that the cart is now empty and therefor destroyed, so clear cart Id
						clearInterval(this.timerInterval);
						this.timeRemaining = null;
						this.cartSummary = null;
						this.cartChanged$.next(false);
						this.localStorage.removeItem('marketplaceCartId');
					}
				},
				error: () => {
					this.toastr.error(
						'An error has occurred, please try again. If the issue persists, please contact Wine Ark.',
						'Error occurred',
					);
				},
			});
	}

	private removeCartItemsByCartItemIdRequestV1(cartItemId: string[]): Observable<CartSummaryModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary?.id}/removeItems`;
		let params = new HttpParams();

		cartItemId.forEach((id) => {
			if (id) {
				params = params.append('cartItemIds', id.toString());
			}
		});

		return this.http.delete<CartSummaryModel>(url, { params });
	}

	/**
	 * Clears the card and removes all references.
	 */
	clearCart(): void {
		this.clearCartV1()
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe({
				next: () => {
					this.cartSummary = null;
					this.localStorage.removeItem('marketplaceCartId');
				},
				error: () => {
					this.toastr.error(
						'An error has occurred, please try again. If the issue persists, please contact Wine Ark.',
						'Error occurred',
					);
				},
			});
	}

	/**
	 * Handles the HTTP request for clearing a cart.
	 * @returns void
	 */
	clearCartV1(): Observable<void> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary?.id}/clear`;
		return this.http.delete<void>(url, {});
	}
	/**
	 * Clears the cart and removes all local references.
	 */
	clearLocalCart(): void {
		this.localStorage.removeItem('marketplaceCartId');
		delete this.cartSummary;
		this.cartChanged$.next(false);
		clearInterval(this.timerInterval);
		this.timeRemaining = null;
		this.timeUnderTenMin = false;
	}

	/**
	 * Called on page load, checks localstorage for a cart Id, and if the user if logged in, then performs merge if required and updates summary.
	 */
	getCartIdFromLocalStorage(): void {
		const cartId = this.localStorage.getItem('marketplaceCartId');
		if (this.authService.userData && this.authService.token) {
			// User is signed in and has a cartId, check if we need to do a merge
			this.mergeCartV1(cartId)
				.pipe(takeUntil(this.ngUnsubscribe$))
				.subscribe({
					next: (summary) => {
						if (summary) {
							this.localStorage.setItem('marketplaceCartId', summary.id);
							this.cartSummary = summary;
							this.setCountdownTimer(summary.expireOn);
							this.cartChanged$.next(true);
							return;
						}
						// There is no cart - most likely user didn't have a cart and the local cart id is expired.
						// So we'll remove the cart Id
						this.localStorage.removeItem('marketplaceCartId');
					},
					error: () => {
						// we dont need an error if it failed to merge, chances are its related to an expired cart
					},
				});
		} else {
			// Only get a cart summary (or check if user has cart) if the user is logged in or there is a cart id
			// Not logged if so only query if there is a cart id in local storage
			if (cartId) {
				this.getCartSummaryV1(cartId)
					.pipe(takeUntil(this.ngUnsubscribe$))
					.subscribe({
						next: (summary) => {
							if (summary) {
								this.localStorage.setItem('marketplaceCartId', summary.id);
								this.cartSummary = summary;
								this.setCountdownTimer(summary.expireOn);
								this.cartChanged$.next(true);
								return;
							}
							// empty / deleted cart id, remove from local storage
							this.localStorage.removeItem('marketplaceCartId');
						},
						error: () => {
							this.localStorage.removeItem('marketplaceCartId');
						},
					});
			}
		}
	}

	getCartIdFromLocalStorageString(): string {
		return this.localStorage.getItem('marketplaceCartId');
	}

	createPurchaseV1(model: CreatePurchaseRequestModel): Observable<CreatePurchaseResponseModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary?.id}/purchase`;
		return this.http.post<CreatePurchaseResponseModel>(url, model);
	}

	createGuestPurchaseV1(
		guestReferenceId: number,
		model: CreatePurchaseRequestModel,
	): Observable<CreatePurchaseResponseModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${this.cartSummary?.id}/guest/${guestReferenceId}/purchase`;
		return this.http.post<CreatePurchaseResponseModel>(url, model);
	}

	prepareCartForPaymentV1(referenceId: string): Observable<void> {
		const url = `${this.baseApiUrl}/v1/MyCart/${referenceId}/pay`;
		return this.http.post<void>(url, {});
	}

	/**
	 * Handles the HTTP request for merging carts.
	 * @param cartId The cart Id found in local storage.
	 * @returns Observable of the summary of the merged cart.
	 */
	private mergeCartV1(cartId: string): Observable<CartSummaryModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${cartId}/merge`;
		return this.http.post<CartSummaryModel>(url, {});
	}

	private setCountdownTimer(expireOn: DateTime): void {
		clearInterval(this.timerInterval);

		if (!(expireOn instanceof DateTime)) {
			expireOn = DateTime.fromISO(expireOn as string);
		}
		this.timerInterval = setInterval(() => {
			const now = DateTime.now();

			if (!expireOn || !DateTime.isDateTime(expireOn)) {
				console.error('expireOn is not a valid DateTime object:', expireOn);
				clearInterval(this.timerInterval);
				return;
			}

			let distance = expireOn.toMillis() - now.toMillis();
			if (distance < 0) {
				clearInterval(this.timerInterval);
				this.timeRemaining = null;
				this.timeUnderTenMin = false;
				this.clearCart();
				const dialogRef = this.dialog.open(NoticeDialogComponent, {
					height: '300',
					width: '700',
					disableClose: true,
					data: {
						title: 'Cart expired',
						message:
							'Oh no, your cart has expired! Please add the items again if you wish to purchase and then proceed to payment.',
						buttonText: 'Ok',
					},
				});
				dialogRef.afterClosed().subscribe((result) => {
					this.router.navigate(['/']);
				});
				return;
			}

			const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
			distance -= minutes * 6000;
			const seconds = Math.floor((distance % (1000 * 60)) / 1000);
			this.timeRemaining = `${minutes >= 10 ? minutes : '0' + minutes}:${seconds >= 10 ? seconds : '0' + seconds}`;
			this.timeUnderTenMin = minutes < 10;
		}, 1000);
	}

	ngOnDestroy() {
		this.unsubscribe();
	}

	unsubscribe() {
		this.ngUnsubscribe$.next(false);
		this.ngUnsubscribe$.unsubscribe();
	}

	isCartVaildPayment(cartId: string): Observable<VaildCartModel> {
		const url = `${this.baseApiUrl}/v1/MyCart/${cartId}/IsVaildForPurchase`;
		return this.http.post<VaildCartModel>(url, {});
	}
}
